import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { FILTER_TEMPLATE_NAME_MAX_LENGTH } from "../types/FilterTemplateTypes";

export interface FilterTemplateSaveDialogProps {
  open: boolean;
  title: string;
  initialName?: string;
  confirmLabel?: string;
  errorMessage?: string | null;
  onClose: () => void;
  onConfirm: (name: string) => void | Promise<void>;
}

const FilterTemplateSaveDialog = ({
  open,
  title,
  initialName = "",
  confirmLabel = "Save",
  errorMessage,
  onClose,
  onConfirm,
}: FilterTemplateSaveDialogProps) => {
  const [name, setName] = useState(initialName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialName.slice(0, FILTER_TEMPLATE_NAME_MAX_LENGTH));
      setLocalError(null);
      setIsSubmitting(false);
      setHasAttemptedSubmit(false);
    }
  }, [initialName, open]);

  const validateName = (value: string): string | null => {
    const trimmedName = value.trim();

    if (!trimmedName) {
      return "Template name is required.";
    }

    if (trimmedName.length > FILTER_TEMPLATE_NAME_MAX_LENGTH) {
      return `Template name must be ${FILTER_TEMPLATE_NAME_MAX_LENGTH} characters or fewer.`;
    }

    return null;
  };

  const handleConfirm = async () => {
    setHasAttemptedSubmit(true);

    const validationError = validateName(name);
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    const trimmedName = name.trim();
    setIsSubmitting(true);
    setLocalError(null);

    try {
      await onConfirm(trimmedName);
    } catch (error) {
      setLocalError(
        error instanceof Error ? error.message : "Unable to save template.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={(theme) => ({
            backgroundColor:
              theme.palette.mode === "light"
                ? theme.palette.primary.main
                : "#181F2A",
            color:
              theme.palette.mode === "light"
                ? theme.palette.primary.contrastText
                : "#fff",
          })}>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          required
          fullWidth
          margin="dense"
          label="Template name"
          value={name}
          onChange={(event) => {
            const nextName = event.target.value.slice(
              0,
              FILTER_TEMPLATE_NAME_MAX_LENGTH,
            );
            setName(nextName);

            if (localError) {
              setLocalError(null);
            }
          }}
          onBlur={() => {
            if (hasAttemptedSubmit) {
              setLocalError(validateName(name));
            }
          }}
          inputProps={{ maxLength: FILTER_TEMPLATE_NAME_MAX_LENGTH }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void handleConfirm();
            }
          }}
          error={Boolean(localError || errorMessage)}
          helperText={localError || errorMessage || " "}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting} sx={{ textTransform: "capitalize" }}>
          Cancel
        </Button>
        <Button
        sx={{ textTransform: "capitalize" }}
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={isSubmitting}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterTemplateSaveDialog;
